import { useState } from 'react';
import { Anchor } from './MarketingShell';
import { Band, Card } from './sections';
import { CONTACT, hasBookingLink, enquiryMailto, type Enquiry } from './contact';
import { PILOTS, PILOT_CREDIT_DAYS, money } from '@inbrief/shared';

const INTERESTS = [
  'A demonstration',
  'A paid pilot',
  'Pricing for a PCN',
  'The founding-customer cohort',
  'InBrief Services',
  'Something else',
];

const EMPTY: Enquiry = {
  name: '', organisation: '', email: '', practices: '',
  interest: INTERESTS[0]!, message: '',
};

/**
 * Booking a demonstration, and everything else that needs a person.
 *
 * The form composes an email rather than posting anywhere. There is no
 * enquiry endpoint, and a form that quietly discards what somebody typed is
 * worse than no form: they would believe they had been in touch. This hands
 * the message to their own mail client with every field filled in, so the
 * sender keeps a copy and knows it went. The address is shown in plain sight
 * next to it for anyone who would rather write their own.
 */
export function ContactPage() {
  const [enquiry, setEnquiry] = useState<Enquiry>(EMPTY);
  const [sent, setSent] = useState(false);

  const set = (field: keyof Enquiry) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => setEnquiry((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Band
        title="Book a demonstration"
        heading="h1"
        lede="Half an hour, against a specification your organisation actually holds. We upload it,
              extract it, and you see what it finds — and what it misses. That is a more honest
              test than any example we could pick ourselves."
      >
        <div className="mk-contact">
          <form
            className="mk-form"
            onSubmit={(e) => {
              e.preventDefault();
              window.location.href = enquiryMailto(enquiry);
              setSent(true);
            }}
          >
            <h3 className="mk-card-title">Tell us what you need</h3>

            <div className="field">
              <label className="field-label" htmlFor="c-name">Your name</label>
              <input id="c-name" required autoComplete="name"
                value={enquiry.name} onChange={set('name')} />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="c-org">Practice or PCN</label>
              <input id="c-org" required autoComplete="organization"
                value={enquiry.organisation} onChange={set('organisation')} />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="c-email">Email address</label>
              <input id="c-email" type="email" required autoComplete="email"
                value={enquiry.email} onChange={set('email')} />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="c-practices">How many practices?</label>
              <input id="c-practices" inputMode="numeric"
                value={enquiry.practices} onChange={set('practices')} />
              <p className="field-hint">Used to work out PCN pricing. Leave blank if you are not sure.</p>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="c-interest">What are you interested in?</label>
              <select id="c-interest" value={enquiry.interest} onChange={set('interest')}>
                {INTERESTS.map((option) => <option key={option}>{option}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="c-message">Anything else</label>
              <textarea id="c-message" rows={4}
                value={enquiry.message} onChange={set('message')} />
              <p className="field-hint">
                Which contract or specification you would like us to use is the most useful thing
                to tell us.
              </p>
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
              Open this in my email
            </button>

            <p className="field-hint mk-form-note">
              This opens your own email application with the message filled in — nothing is sent
              from this page, and nothing you type here is stored. Prefer to write it yourself?
              The address is on the right.
            </p>

            {sent ? (
              <div className="note-box" role="status">
                Your email application should have opened with the message ready. If nothing
                happened, write to <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> instead.
              </div>
            ) : null}
          </form>

          <aside className="mk-contact-side">
            <Card title="Contact us directly">
              <dl className="mk-contact-details">
                <dt>Email</dt>
                <dd><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></dd>
                <dt>Response</dt>
                <dd>We aim to reply {CONTACT.responseTime}.</dd>
              </dl>
              {hasBookingLink ? (
                <a className="btn btn-secondary" href={CONTACT.bookingUrl}
                   target="_blank" rel="noopener noreferrer">
                  Pick a slot in the diary
                </a>
              ) : null}
            </Card>

            <Card title="Already a customer?">
              <p>
                Support requests go to the same address. To use the product,{' '}
                <Anchor to="/login">log in</Anchor>.
              </p>
            </Card>

            <Card title="Want to look first?">
              <p>
                The interactive demonstration needs no account and no conversation.
              </p>
              <p className="mk-contact-cta">
                <Anchor to="/demo" className="btn btn-secondary btn-small">Try the demo</Anchor>
              </p>
            </Card>
          </aside>
        </div>
      </Band>

      <Band title="Pilots" tone="tint">
        <div className="mk-two">
          <Card title="What a pilot is">
            <p>
              Twelve weeks with your own contracts, your own staff and your own patient
              communications — long enough to cross a real reporting cycle rather than a
              demonstration script.
            </p>
          </Card>
          <Card title="What it costs">
            <ul className="mk-list">
              {PILOTS.map((pilot) => (
                <li key={pilot.id}>
                  <strong>{pilot.label}</strong>, {pilot.weeks} weeks — {money(pilot.priceGbp)}
                </li>
              ))}
            </ul>
            <p className="mk-small">
              Credited against an annual subscription if you convert within {PILOT_CREDIT_DAYS} days.
            </p>
          </Card>
        </div>
      </Band>
    </>
  );
}
